import { useTranslation } from 'react-i18next';
import { useEffect, useState, useCallback } from 'react';
import { productsApi } from '@/lib/api/arrex/catalog';
import { settingsApi } from '@/lib/api/arrex/settings';
import { useApp } from '@/contexts/AppContext';
import { localized } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { ToggleField } from '@/components/ui/toggle-field';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowUp, ArrowDown, Trash2, Save, RotateCcw, Loader2 } from 'lucide-react';
import { rowImg } from "@/lib/format";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface NewArrivalRow {
  /** row id in new_arrivals table — undefined for rows not yet persisted */
  id?: string;
  product_id: string;
  is_visible: boolean;
  sort_order: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
async function fetchAllProducts(): Promise<any[]> {
  const all: any[] = [];
  let page = 1;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { items, totalPages } = await productsApi.adminList({ page, pageSize: 60, sort: 'newest' });
    all.push(...(items || []));
    if (page >= (totalPages || 1)) break;
    page++;
  }
  return all;
}

function listEqual(a: NewArrivalRow[], b: NewArrivalRow[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((r, i) =>
    r.product_id === b[i].product_id &&
    r.is_visible === b[i].is_visible,
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function AdminNewArrivals() {
  const { t } = useTranslation();
  const { lang } = useApp();

  const [products, setProducts] = useState<any[]>([]);
  /** Authoritative list fetched from the server */
  const [saved, setSaved] = useState<NewArrivalRow[]>([]);
  /** Local working copy — all edits happen here first */
  const [draft, setDraft] = useState<NewArrivalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // -------------------------------------------------------------------------
  // Load
  // -------------------------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ps, na] = await Promise.all([
        fetchAllProducts(),
        settingsApi.adminNewArrivals(),
      ]);
      setProducts(ps);
      const rows: NewArrivalRow[] = (na.items || []).map((r: any) => ({
        id: r.id,
        product_id: r.product_id,
        is_visible: r.is_visible === true || r.is_visible === 1 || r.is_visible === '1',
        sort_order: r.sort_order ?? 0,
      }));
      setSaved(rows);
      setDraft(rows);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // -------------------------------------------------------------------------
  // Draft helpers
  // -------------------------------------------------------------------------
  const isDirty = !listEqual(draft, saved);
  const inDraft = (pid: string) => draft.some(r => r.product_id === pid);

  /** Toggle a product in/out of the draft list */
  const toggle = (pid: string) => {
    setDraft(prev => {
      if (prev.some(r => r.product_id === pid)) {
        // Remove from draft
        return prev.filter(r => r.product_id !== pid);
      } else {
        // Add to draft at the end
        return [
          ...prev,
          { product_id: pid, is_visible: true, sort_order: prev.length },
        ];
      }
    });
  };

  /** Move an item up (-1) or down (+1) within the draft */
  const move = (idx: number, dir: -1 | 1) => {
    const next = idx + dir;
    setDraft(prev => {
      if (next < 0 || next >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[next]] = [copy[next], copy[idx]];
      return copy;
    });
  };

  /** Toggle visibility for a draft item */
  const setVisible = (pid: string, v: boolean) => {
    setDraft(prev => prev.map(r => r.product_id === pid ? { ...r, is_visible: v } : r));
  };

  /** Remove from draft (trash icon) */
  const remove = (pid: string) => {
    setDraft(prev => prev.filter(r => r.product_id !== pid));
  };

  /** Discard draft changes */
  const reset = () => setDraft(saved);

  // -------------------------------------------------------------------------
  // Save — reconcile draft vs. saved
  // -------------------------------------------------------------------------
  const save = async () => {
    setSaving(true);
    try {
      const savedIds = new Set(saved.map(r => r.product_id));
      const draftIds = new Set(draft.map(r => r.product_id));

      // 1. Add products that are in draft but not in saved
      for (const row of draft) {
        if (!savedIds.has(row.product_id)) {
          await settingsApi.addNewArrival(row.product_id);
        }
      }

      // 2. Remove products that were in saved but removed from draft
      for (const row of saved) {
        if (!draftIds.has(row.product_id)) {
          if (row.id) {
            await settingsApi.removeNewArrival(row.id);
          } else {
            await settingsApi.removeNewArrivalByProduct(row.product_id);
          }
        }
      }

      // 3. After adds/removes, reload to get fresh IDs for newly added rows
      const freshNa = await settingsApi.adminNewArrivals();
      const freshRows: NewArrivalRow[] = (freshNa.items || []).map((r: any) => ({
        id: r.id,
        product_id: r.product_id,
        is_visible: r.is_visible === true || r.is_visible === 1 || r.is_visible === '1',
        sort_order: r.sort_order ?? 0,
      }));

      // 4. Build the reorder array matching current draft order
      const draftOrderedPids = draft.map(r => r.product_id);
      const reorderIds: string[] = draftOrderedPids
        .map(pid => freshRows.find(r => r.product_id === pid)?.id)
        .filter(Boolean) as string[];

      if (reorderIds.length > 0) {
        await settingsApi.reorderNewArrivals(reorderIds);
      }

      // 5. Sync visibility for items that changed
      for (const dRow of draft) {
        const freshRow = freshRows.find(r => r.product_id === dRow.product_id);
        if (!freshRow) continue;
        const savedRow = saved.find(r => r.product_id === dRow.product_id);
        if (savedRow && savedRow.is_visible !== dRow.is_visible) {
          await settingsApi.updateNewArrival(freshRow.id!, { is_visible: dRow.is_visible });
        } else if (!savedRow) {
          // Newly added row — set its visibility if it's not the default true
          if (!dRow.is_visible) {
            await settingsApi.updateNewArrival(freshRow.id!, { is_visible: false });
          }
        }
      }

      toast.success('Changes saved successfully');
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------
  const productById = (pid: string) => products.find(p => p.id === pid);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="font-display text-3xl font-bold">{t('admin.newArrivals.title')}</h1>

        {isDirty && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={reset} disabled={saving}>
              <RotateCcw className="w-4 h-4 mr-1" />
              {t('admin.common.discard')}
            </Button>
            <Button size="sm" onClick={save} disabled={saving}>
              {saving
                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                : <Save className="w-4 h-4 mr-1" />}
              {t('admin.common.saveChanges')}
            </Button>
          </div>
        )}
      </div>

      {/* Unsaved changes banner */}
      {isDirty && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-amber-700 dark:text-amber-300">
          {t('admin.common.unsaved')}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* ---------------------------------------------------------------- */}
        {/* LEFT — Curated List                                              */}
        {/* ---------------------------------------------------------------- */}
        <div className="surface-card rounded-lg p-4">
          <h2 className="font-display text-lg mb-3">
            {t('admin.curated.list')} ({draft.length})
          </h2>

          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('admin.common.loading')}
            </div>
          ) : (
            <div className="space-y-2">
              {draft.map((row, idx) => {
                const p = productById(row.product_id);
                return (
                  <div
                    key={row.product_id}
                    className="rounded border border-border p-2 flex flex-col gap-1"
                  >
                    {/* Top row: image + name */}
                    <div className="flex items-center gap-2 min-w-0">
                      {rowImg(p) && (
                        <img
                          src={rowImg(p)}
                          className="w-10 h-10 rounded object-cover flex-shrink-0"
                          alt=""
                        />
                      )}
                      <span className="text-sm font-medium leading-snug break-words min-w-0">
                        {p ? localized(p, 'name', lang) : <span className="text-muted-foreground">—</span>}
                      </span>
                    </div>

                    {/* Bottom row: controls */}
                    <div className="flex items-center gap-1 ps-12">
                      <ToggleField
                        checked={row.is_visible}
                        onCheckedChange={(v) => setVisible(row.product_id, v)}
                        labelPosition="end"
                        className="me-auto"
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        title={t('admin.common.moveUp')}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => move(idx, 1)}
                        disabled={idx === draft.length - 1}
                        title={t('admin.common.moveDown')}
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => remove(row.product_id)}
                        title={t('admin.common.remove')}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                );
              })}

              {draft.length === 0 && (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  {t('admin.curated.emptyProducts')}
                </p>
              )}
            </div>
          )}
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* RIGHT — All Products picker                                      */}
        {/* ---------------------------------------------------------------- */}
        <div className="surface-card rounded-lg p-4">
          <h2 className="font-display text-lg mb-3">{t('admin.curated.allProducts')}</h2>
          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
            {products.map(p => {
              const checked = inDraft(p.id);
              return (
                <label
                  key={p.id}
                  className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors select-none ${
                    checked ? 'bg-primary/10' : 'hover:bg-muted/50'
                  }`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(p.id)}
                    className="flex-shrink-0"
                  />
                  {rowImg(p) && (
                    <img
                      src={rowImg(p)}
                      className="w-8 h-8 rounded object-cover flex-shrink-0"
                      alt=""
                    />
                  )}
                  <span className="text-sm leading-snug break-words min-w-0">
                    {localized(p, 'name', lang)}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
